/**
 * Five StatCards + AreaChart showing activity over the filter
 * window. Driven by `activity.summary` RPC.
 *
 * Layout: 5 stat cards in a row, then the time-series chart below.
 * The "Top type" card surfaces the single most-frequent event
 * type — useful sanity check that capture is firing for the
 * surfaces an admin expects.
 */

import { Activity } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { StatCard } from "@/components/shared/StatCard";

import type { ActivitySummaryResult } from "@/lib/activity-rpc";

interface Props {
  summary: ActivitySummaryResult | null;
  loading: boolean;
}

export function ActivitySummary({ summary, loading }: Props) {
  const t = summary?.totals;
  const series = summary?.series ?? [];
  const topType = t ? pickTopType(t.by_type) : null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard
          title="Events"
          value={loading ? "…" : fmtCount(t?.total ?? 0)}
          sparkData={series.map((s) => ({ value: s.count }))}
          sparkColor="hsl(var(--primary))"
        />
        <StatCard
          title="Sessions"
          value={loading ? "…" : fmtCount(t?.sessions ?? 0)}
        />
        <StatCard
          title="Errors"
          value={loading ? "…" : fmtCount(t?.errors ?? 0)}
          sparkData={series.map((s) => ({ value: s.errors }))}
          sparkColor="hsl(0, 80%, 60%)"
        />
        <StatCard
          title="Harness runs"
          value={loading ? "…" : fmtCount(t?.harness_runs ?? 0)}
        />
        <StatCard
          title="Top type"
          value={loading ? "…" : topType ?? "—"}
        />
      </div>

      {series.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="mb-2 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <Activity className="h-3.5 w-3.5" />
            Events per bucket (errors overlay)
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <AreaChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <XAxis
                dataKey="ts"
                tickFormatter={fmtTick}
                stroke="currentColor"
                fontSize={10}
                tick={{ fill: "currentColor" }}
              />
              <YAxis stroke="currentColor" fontSize={10} tick={{ fill: "currentColor" }} />
              <Tooltip
                formatter={(value: number, name: string) => [value, name]}
                labelFormatter={(label: number) => new Date(label).toLocaleString()}
                contentStyle={{ fontSize: "12px" }}
              />
              <Area
                type="monotone"
                dataKey="count"
                name="events"
                stroke="hsl(220, 80%, 55%)"
                fill="hsl(220, 80%, 55%)"
                fillOpacity={0.25}
              />
              <Area
                type="monotone"
                dataKey="errors"
                name="errors"
                stroke="hsl(0, 80%, 55%)"
                fill="hsl(0, 80%, 55%)"
                fillOpacity={0.4}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtTick(ts: number): string {
  const d = new Date(ts);
  // Hour-level bucket → HH:00; day-level bucket → MMM-DD.
  if (d.getHours() === 0 && d.getMinutes() === 0) {
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function pickTopType(byType: Record<string, number>): string | null {
  let topKey: string | null = null;
  let topVal = 0;
  for (const [k, v] of Object.entries(byType)) {
    if (v > topVal) {
      topVal = v;
      topKey = k;
    }
  }
  return topKey;
}
